import { supabase } from '../supabase';

/**
 * Generates the next week for a client's program by calling the server-side
 * Edge Function, which runs with service-role access and bypasses RLS.
 *
 * Always clones from sourceWeekNumber so coach edits flow forward naturally.
 *
 * @param {string|number} programId       - The client's program clone ID
 * @param {number}        sourceWeekNumber - The week number to clone FROM
 * @returns {Promise<{ success: boolean, newWeekNumber: number, alreadyExisted: boolean }>}
 */
export const generateNextWeek = async (programId, sourceWeekNumber) => {
  // Try to get a fresh token — prefer refreshSession, fall back to getSession
  let accessToken = null;
  try {
    const { data: refreshData } = await supabase.auth.refreshSession();
    accessToken = refreshData?.session?.access_token ?? null;
  } catch {
    // refreshSession can fail if refresh token is expired; fall back to current session
  }
  if (!accessToken) {
    const { data: sessionData } = await supabase.auth.getSession();
    accessToken = sessionData?.session?.access_token ?? null;
  }

  const { data, error } = await supabase.functions.invoke('generate-week', {
    body: { programId, sourceWeekNumber },
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });

  if (error) {
    // Extract the real error message from the edge function response body
    let message = error.message || 'Edge function error';
    try {
      const body = await error.context?.json?.();
      if (body?.error) message = body.error;
    } catch {}
    throw new Error(message);
  }
  if (!data?.success) throw new Error(data?.error || 'Week generation failed');

  return data;
};

const weekGeneratorExport = { generateNextWeek };
export default weekGeneratorExport;
